import React, { useRef, useState, useEffect } from 'react'
import {
  EduButton,
  notification,
  captureSentryException,
} from '@edulastic/common'
import { segmentApi } from '@edulastic/api'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { push } from 'connected-react-router'
import {
  StyledPDLDrawer,
  StyledButtonText,
  StyledPDLErrorContainer,
  StyledPDLErrorHeading,
  StyledPDLErrorDescription,
  StyledPDLErrorButtonGroup,
} from './styled'
import UnVerifiedUserWarningModal from '../../../../common/components/UnVerifiedUserWarningModal'
import {
  getEmailVerified,
  getVerificationTS,
  isDefaultDASelector,
  isSignupUsingUNAndPassSelector,
} from '../../../../student/Login/ducks'
import { BULK_CONVERTER_SEGMENT } from '../../../constants/bulkConverterSegment'
import { buildMyContentRouteAfterPdlUpload } from '../../../TestList/utils/pdlBulkUploadHandoff'
import { isContentConvertEnabledSelector } from '../../../src/selectors/user'

const BulkConvertDrawer = ({
  visible,
  onClose,
  push: pushRoute,
  children,
  emailVerified,
  verificationTS,
  isDefaultDA,
  isSignupUsingUNAndPass,
  source,
  isContentConvertEnabled,
}) => {
  const previousNode = useRef(null)
  const retryButtonRef = useRef(null)
  const [hasError, setHasError] = useState(false)
  const [showDrawer, setShowDrawer] = useState(!!visible)
  const [
    showUnVerifiedUserWarningModal,
    setShowUnVerifiedUserWarningModal,
  ] = useState(false)

  const closeDrawer = ({ isClickEvent = true } = {}) => {
    if (isClickEvent) {
      segmentApi.genericEventTrack(BULK_CONVERTER_SEGMENT.PDL_DRAWER_CLOSE, {
        source,
      })
    }
    onClose?.()
    setShowDrawer(false)
  }

  const handleUploadComplete = (conversion) => {
    segmentApi.genericEventTrack(
      BULK_CONVERTER_SEGMENT.CONVERT_UPLOAD_COMPLETE,
      {
        source,
      }
    )
    // Navigate via Redux + connected-react-router so this always uses the same history
    // instance as ConnectedRouter (Pear may invoke this outside normal React timing).
    // Push before closing the drawer so unmount/onClose cannot prevent the transition.
    pushRoute(
      buildMyContentRouteAfterPdlUpload(conversion || {}, {
        reviewTabsEnabled: isContentConvertEnabled,
      })
    )
    closeDrawer({ isClickEvent: false })
  }

  useEffect(() => {
    if (visible) {
      setShowDrawer(true)
    }
  }, [visible])

  useEffect(() => {
    if (showDrawer) {
      setHasError(false)
      segmentApi.genericEventTrack(
        BULK_CONVERTER_SEGMENT.CREATE_DOCUMENT_CLICK,
        {
          source,
        }
      )
    }
  }, [showDrawer])

  // Focus management: focus on retry button when error appears
  useEffect(() => {
    if (hasError && retryButtonRef.current) {
      setTimeout(() => {
        retryButtonRef.current?.focus()
      }, 100)
    }
  }, [hasError])

  const renderBulkConverter = (target) => {
    const showBulkContentConverter =
      window?.pear?.bulkConverter?.showBulkContentConverter

    if (showBulkContentConverter) {
      try {
        showBulkContentConverter({
          target,
          onClickClose: closeDrawer,
          productType: 'EDULASTIC',
          onUploadComplete: handleUploadComplete,
        })
        setHasError(false)
      } catch (error) {
        console.error('Error opening bulk content converter:', error)
        captureSentryException(error, {
          pearBulkConverter: {
            phase: 'showBulkContentConverter',
          },
        })
        setHasError(true)
        notification({
          type: 'error',
          msg: 'Unable to open bulk content converter. Please try again.',
        })
      }
    } else {
      captureSentryException(
        new Error('[Pear BulkConverter] Script not loading'),
        {
          hasWindowPear: typeof window !== 'undefined' && !!window.pear,
          hasBulkConverter: !!window?.pear?.bulkConverter,
          hasShowBulkContentConverter: !!showBulkContentConverter,
        }
      )
      setHasError(true)
      notification({
        type: 'error',
        msg:
          'Bulk content converter is currently unavailable. Please try again later.',
      })
    }
  }

  const handleRetry = () => {
    setHasError(false)
    if (previousNode.current) {
      renderBulkConverter(previousNode.current)
    }
  }

  const handleCancel = closeDrawer

  const shouldPromptEmailVerification =
    !emailVerified && (verificationTS || isSignupUsingUNAndPass) && !isDefaultDA

  const showDrawerFromChildren = () => {
    if (shouldPromptEmailVerification) {
      setShowUnVerifiedUserWarningModal(true)
    } else {
      setShowDrawer(true)
    }
  }

  return (
    <>
      {showUnVerifiedUserWarningModal && (
        <UnVerifiedUserWarningModal
          visible={showUnVerifiedUserWarningModal}
          onCloseModal={() => setShowUnVerifiedUserWarningModal(false)}
          closeButtonText="Go Back"
        />
      )}
      {children && (
        <span style={{ cursor: 'pointer' }} onClick={showDrawerFromChildren}>
          {children}
        </span>
      )}
      <StyledPDLDrawer
        title={null}
        placement="right"
        width="650px"
        closable={false}
        maskClosable={false}
        keyboard={false}
        onClose={closeDrawer}
        visible={showDrawer}
        destroyOnClose
      >
        {hasError ? (
          <StyledPDLErrorContainer
            role="alert"
            aria-live="assertive"
            aria-labelledby="bulk-convert-error-heading"
            aria-describedby="bulk-convert-error-description"
          >
            <StyledPDLErrorHeading id="bulk-convert-error-heading">
              Unable to Load Bulk Content Converter
            </StyledPDLErrorHeading>
            <StyledPDLErrorDescription id="bulk-convert-error-description">
              The bulk content converter is currently unavailable. Please try
              again or cancel to go back.
            </StyledPDLErrorDescription>
            <StyledPDLErrorButtonGroup role="group" aria-label="Error actions">
              <EduButton
                ref={retryButtonRef}
                width="120px"
                data-cy="bulkConvertErrorRetryButton"
                aria-label="Retry loading bulk content converter"
                tabIndex={0}
                onClick={handleRetry}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleRetry()
                  }
                }}
              >
                <StyledButtonText>Retry</StyledButtonText>
              </EduButton>
              <EduButton
                width="120px"
                data-cy="bulkConvertErrorCancelButton"
                aria-label="Cancel and close bulk content converter drawer"
                tabIndex={0}
                isGhost
                onClick={handleCancel}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleCancel()
                  }
                }}
              >
                <StyledButtonText>Cancel</StyledButtonText>
              </EduButton>
            </StyledPDLErrorButtonGroup>
          </StyledPDLErrorContainer>
        ) : (
          <BulkConverterContent
            previousNodeRef={previousNode}
            renderBulkConverter={renderBulkConverter}
          />
        )}
      </StyledPDLDrawer>
    </>
  )
}

const BulkConverterContent = ({ previousNodeRef, renderBulkConverter }) => {
  const setRef = (node) => {
    if (node !== null && node !== previousNodeRef.current) {
      previousNodeRef.current = node
      renderBulkConverter(node)
    }
  }

  useEffect(() => {
    return () => {
      if (previousNodeRef.current) {
        previousNodeRef.current.replaceChildren?.()
      }
    }
  }, [])

  return <div ref={setRef} style={{ width: '100%', height: '100vh' }} />
}

const enhance = compose(
  connect(
    (state) => ({
      emailVerified: getEmailVerified(state),
      verificationTS: getVerificationTS(state),
      isDefaultDA: isDefaultDASelector(state),
      isSignupUsingUNAndPass: isSignupUsingUNAndPassSelector(state),
      isContentConvertEnabled: isContentConvertEnabledSelector(state),
    }),
    { push }
  )
)
export default enhance(BulkConvertDrawer)
